import { AddCircleOutlineRounded } from '@mui/icons-material'
import { Box, Chip, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import GenericTable from 'src/components/table/GenericTable'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { GridColumns } from '@mui/x-data-grid-pro'
import { useCallback, useMemo } from 'react'
import { useGetSystemProjectAdmins } from 'src/api/user'
import { useDialogCtx, useUserCtx } from 'src/hooks/context-hooks'
import ProjectAdminDialog from '../../ProjectAdminDialog'
import { AuthorizationProvider } from 'src/components/common/Authorization'
import { permissionValue } from 'src/enums/permissionValue'
import { AuthorizationType } from 'src/enums/auth'
import { UserStatus } from 'src/enums/user-status'

const ProjectAdminUsers = () => {
  const { t: translate } = useTranslation(['projects'])
  const theme = useTheme()
  const { data: projectAdmins, isLoading } = useGetSystemProjectAdmins()
  const { showDialog } = useDialogCtx()
  const { user } = useUserCtx()

  const onAddUser = useCallback(() => {
    showDialog(ProjectAdminDialog, {
      title: translate('add-project-admin-user'),
      opts: {
        maxWidth: 'sm',
        fullWidth: true,
      },
    })
  }, [translate, showDialog])

  const columns: GridColumns = useMemo(
    () => [
      { field: 'firstName', headerName: translate('firstName', { ns: 'users' }), flex: 1 },
      { field: 'lastName', headerName: translate('lastName', { ns: 'users' }), flex: 1 },
      { field: 'email', headerName: translate('email', { ns: 'users' }), flex: 2 },
      { field: 'phoneNumber', headerName: translate('phoneNumber', { ns: 'users' }), flex: 1 },
      {
        field: 'status',
        headerName: translate('status', { ns: 'users' }),
        flex: 1,
        renderCell: ({ value }) => (
          <Chip
            label={
              value == UserStatus.Pending
                ? translate('invited', { ns: 'users' })
                : translate(UserStatus[value]?.toString(), { ns: 'users' })
            }
            sx={{
              background:
                value == UserStatus.Pending
                  ? `${getThemeColor(theme, MUIThemeColors.warningMain)}44`
                  : value == UserStatus.Complete
                  ? `${getThemeColor(theme, MUIThemeColors.successMain)}44`
                  : `${getThemeColor(theme, MUIThemeColors.errorMain)}44`,
              color:
                value == UserStatus.Pending
                  ? getThemeColor(theme, MUIThemeColors.warningDark)
                  : value == UserStatus.Complete
                  ? getThemeColor(theme, MUIThemeColors.successDark)
                  : getThemeColor(theme, MUIThemeColors.errorDark),
            }}
          />
        ),
      },
    ],
    [translate, theme],
  )

  return (
    <CommonPageLayout
      titleSection={{ title: translate('system-project-admin-users') }}
      topAction={[
        <AuthorizationProvider
          key="project-add-user-button"
          permission={user?.permission}
          permissions={[permissionValue.SystemAdminPermission, permissionValue.SystemProjectAdminPermission]}
          self_auth={true}
          type={AuthorizationType.Disabled}
        >
          <GridActionButton
            buttonText={translate('add-user')}
            buttonIcon={<AddCircleOutlineRounded />}
            onClick={onAddUser}
          />
        </AuthorizationProvider>,
      ]}
    >
      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        <GenericTable
          id="users-table"
          data={projectAdmins ?? []}
          getRowHeight={() => 'auto'}
          columns={columns}
          enableToolbar={true}
          loading={isLoading}
          autoHeight={false}
          height={'81vh'}
        />
      </Box>
    </CommonPageLayout>
  )
}

export default ProjectAdminUsers
